/* eslint-disable jsx-a11y/media-has-caption */
import { Box, Container, Typography, Button, Chip, Link } from '@mui/material';
import Head from 'next/head';
import Image from 'next/image';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import StarIcon from '@mui/icons-material/Star';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import Warning from '@mui/icons-material/Warning';
import Logo from '@common/assets/svgs/Logo';
import Routes from '@common/defs/routes';
import withAuth, { AUTH_MODE } from '@modules/auth/hocs/withAuth';
import { useTracking } from '@common/hooks/useTracking';
import { usePwaContext } from '@common/contexts/PwaContext';
import { isFacebookWithBrokenStorage } from '@common/utils/storage';
import LpSharedSections from '@modules/lp/components/LpSharedSections';
import { NextPageWithLayout } from '../_app';

const PRIMARY = 'rgb(11, 90, 240)';
const ACCENT = '#04c25d';

// Wrapper qui fait apparaître son contenu en fade + slide-up au scroll
const ScrollReveal = ({
  children,
  delay = 0,
  y = 24,
}: {
  children: React.ReactNode;
  delay?: number;
  y?: number;
}) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : `translateY(${y}px)`,
        transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </Box>
  );
};

const LandingPageV2: NextPageWithLayout<Record<string, unknown>> = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const router = useRouter();
  const { isPwa } = usePwaContext();
  const { trackEvent } = useTracking();
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    setIsBlocked(isFacebookWithBrokenStorage());
    trackEvent('lp2.page_view');
  }, [trackEvent]);

  // Canvas mesh gradient animé (repris de /lp, ajusté)
  useEffect(() => {
    if (isBlocked) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    let w: number;
    let h: number;
    let dpr: number;
    let running = true;
    let animationId: number;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mouse = { x: 0.5, y: 0.5 };
    const BLOBS = [
      { r: 4, g: 194, b: 93 },
      { r: 11, g: 90, b: 240 },
      { r: 4, g: 194, b: 93 },
      { r: 11, g: 90, b: 240 },
      { r: 56, g: 189, b: 248 },
      { r: 4, g: 194, b: 93 },
      { r: 11, g: 90, b: 240 },
    ].map((color) => ({
      color,
      baseX: Math.random(),
      baseY: Math.random(),
      ampX: 0.08 + Math.random() * 0.12,
      ampY: 0.08 + Math.random() * 0.12,
      r: 180 + Math.random() * 260,
      speed: 0.08 + Math.random() * 0.18,
      phase: Math.random() * Math.PI * 2,
      alpha: 0.12 + Math.random() * 0.12,
      parallax: 0.06 + Math.random() * 0.1,
    }));

    const resize = () => {
      dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
      w = Math.floor(window.innerWidth);
      h = Math.floor(window.innerHeight);
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize, { passive: true });

    const onMove = (e: MouseEvent | TouchEvent) => {
      const clientX = 'clientX' in e ? e.clientX : e.touches?.[0]?.clientX ?? w / 2;
      const clientY = 'clientY' in e ? e.clientY : e.touches?.[0]?.clientY ?? h / 2;
      mouse.x = clientX / w;
      mouse.y = clientY / h;
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });

    const render = (time: number) => {
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';
      for (const b of BLOBS) {
        const px =
          b.baseX + Math.sin(time * b.speed + b.phase) * b.ampX + (mouse.x - 0.5) * b.parallax;
        const py =
          b.baseY +
          Math.cos(time * b.speed * 0.9 + b.phase) * b.ampY +
          (mouse.y - 0.5) * b.parallax;
        const cx = px * w;
        const cy = py * h;
        const radius = b.r * (0.85 + 0.15 * Math.sin(time * b.speed + b.phase));
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
        grad.addColorStop(0, `rgba(${b.color.r},${b.color.g},${b.color.b},${b.alpha})`);
        grad.addColorStop(1, `rgba(${b.color.r},${b.color.g},${b.color.b},0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';
    };

    const tick = (now: number) => {
      if (!running || reduce.matches) {
        return;
      }
      render(now / 1000);
      animationId = requestAnimationFrame(tick);
    };

    const handleVisibilityChange = () => {
      running = !document.hidden;
      if (running && !reduce.matches) {
        animationId = requestAnimationFrame(tick);
      } else {
        cancelAnimationFrame(animationId);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    if (!reduce.matches) {
      animationId = requestAnimationFrame(tick);
    }
    return () => {
      running = false;
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('touchmove', onMove);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isBlocked]);

  if (isBlocked) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          textAlign: 'center',
          bgcolor: '#fff',
        }}
      >
        <Box sx={{ maxWidth: 480 }}>
          <Warning color="error" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Attention
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '1.1rem' }}>
            Cette page ne peut pas s&apos;afficher correctement dans le navigateur Facebook.
            <br />
            <br />
            Veuillez l&apos;ouvrir dans votre navigateur habituel (ex : <strong>
              Chrome
            </strong> ou <strong>Safari</strong>) pour continuer.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isPwa) {
    router.replace(Routes.Auth.Register);
  }

  const goToRegister = (source: string) => {
    trackEvent(`lp2.cta_click.${source}`);
    router.push({
      pathname: Routes.Auth.Register,
      query: router.query,
    });
  };

  return (
    <>
      <Head>
        <title>AlloClients — Le réseau qui mutualise la visibilité des praticiens</title>
        <meta
          name="description"
          content="Rejoignez le premier réseau de praticiens du bien-être. Mutualisez votre visibilité, recevez des demandes locales, remplissez votre agenda."
        />
      </Head>

      <canvas
        ref={canvasRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: -1,
          pointerEvents: 'none',
          filter: 'blur(60px) saturate(120%)',
          opacity: 0.4,
        }}
      />

      <Box
        component="main"
        sx={{
          minHeight: '100svh',
          color: 'rgb(30, 30, 30)',
          WebkitFontSmoothing: 'antialiased',
        }}
      >
        {/* Top bar */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            maxWidth: 1180,
            mx: 'auto',
            px: { xs: 2, md: 4 },
            pt: { xs: 2, md: 3 },
          }}
        >
          <Logo sx={{ height: { xs: 32, md: 40 }, maxWidth: '100%' }} />
          <Button
            variant="contained"
            disableElevation
            onClick={() => router.push(Routes.Auth.Login)}
            startIcon={<PersonOutlineIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none',
              fontSize: 13,
              fontWeight: 500,
              color: '#fff',
              bgcolor: '#000',
              boxShadow: 'none',
              '&:hover': { bgcolor: 'rgb(33, 33, 33)', boxShadow: 'none' },
            }}
          >
            Me connecter
          </Button>
        </Box>

        {/* ───────── SECTION 1 — HERO ───────── */}
        <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 10 }, pb: { xs: 6, md: 10 } }}>
          <ScrollReveal>
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
              <Chip
                label="Le premier réseau de praticiens du bien-être"
                size="small"
                sx={{
                  mb: 3,
                  bgcolor: '#fff',
                  border: '1px solid rgba(11,90,240,0.2)',
                  color: PRIMARY,
                  fontWeight: 600,
                  fontSize: 12,
                  px: 1,
                }}
              />
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: '2rem', sm: '2.6rem', md: '3.6rem' },
                  fontWeight: 800,
                  lineHeight: 1.1,
                  letterSpacing: '-0.03em',
                  maxWidth: 920,
                  mx: 'auto',
                  mb: 3,
                  color: '#0f172a',
                }}
              >
                Le premier réseau qui{' '}
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, ${ACCENT} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  mutualise la visibilité
                </Box>{' '}
                des praticiens.
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  color: 'rgb(70, 70, 70)',
                  maxWidth: 720,
                  mx: 'auto',
                  mb: 4,
                  lineHeight: 1.55,
                }}
              >
                Au lieu de tout gérer seul, vous rejoignez un réseau qui finance et partage la
                visibilité, les outils et les ressources pour développer votre pratique.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 4,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => goToRegister('hero')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: { xs: 15, md: 17 },
                    py: { xs: 1.5, md: 1.8 },
                    px: { xs: 4, md: 5 },
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, #0a4ed1 100%)`,
                    boxShadow: '0 8px 24px rgba(11,90,240,0.35)',
                    '&:hover': {
                      background: `linear-gradient(135deg, #0a4ed1 0%, ${PRIMARY} 100%)`,
                      boxShadow: '0 12px 28px rgba(11,90,240,0.45)',
                    },
                  }}
                >
                  Je m&apos;inscris gratuitement
                </Button>
                <Typography sx={{ fontSize: 13, color: 'rgb(100, 100, 100)' }}>
                  Inscription en 2 minutes · Sans carte bancaire
                </Typography>
              </Box>

              {/* Social proof */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.5,
                  bgcolor: 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(0,0,0,0.06)',
                  borderRadius: 999,
                  px: 2,
                  py: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {[
                    'https://framerusercontent.com/images/iVqUKcOjEIhR2xg4c08qfwujSVg.png',
                    'https://framerusercontent.com/images/GLoPIOdIWtLKJcLTQRSPCcj5F0.png?width=611&height=920',
                    'https://framerusercontent.com/images/ciAFofBBGfNJSJHNtX1eS6vVYM.png',
                  ].map((src, i) => (
                    <Box
                      key={src}
                      sx={{
                        position: 'relative',
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '2px solid #fff',
                        ml: i === 0 ? 0 : '-8px',
                        bgcolor: '#fff',
                      }}
                    >
                      <Image
                        src={src}
                        alt="Praticien du réseau"
                        fill
                        sizes="28px"
                        style={{ objectFit: 'cover' }}
                      />
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.2 }}>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <StarIcon key={i} sx={{ fontSize: 14, color: 'rgb(237, 156, 5)' }} />
                  ))}
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                  +494 praticiens dans le réseau
                </Typography>
              </Box>
            </Box>
          </ScrollReveal>

          {/* Screenshot */}
          <ScrollReveal delay={200} y={32}>
            <Box
              sx={{
                maxWidth: 920,
                mx: 'auto',
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 20px 60px rgba(11,90,240,0.15)',
                bgcolor: '#fff',
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  px: 1.5,
                  py: 1.25,
                  background: 'linear-gradient(#f7f7f7, #efefef)',
                  borderBottom: '1px solid rgba(0,0,0,.08)',
                }}
              >
                <Box sx={{ height: 10, width: 10, borderRadius: '50%', bgcolor: '#e96e64' }} />
                <Box sx={{ height: 10, width: 10, borderRadius: '50%', bgcolor: '#e6c15f' }} />
                <Box sx={{ height: 10, width: 10, borderRadius: '50%', bgcolor: '#69c077' }} />
              </Box>
              <Image
                src="/images/misc/lead-requests-notifs.jpg"
                alt="Interface AlloClients"
                width={1200}
                height={800}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  objectFit: 'cover',
                }}
              />
            </Box>
          </ScrollReveal>
        </Container>

        {/* Sections partagées (Le principe / Démo / Témoignages / CTA final) */}
        <LpSharedSections onCtaClick={goToRegister} />

        {/* Footer */}
        <Box sx={{ textAlign: 'center', pb: 4 }}>
          <Link href="/legal/politique-de-confidentialite" sx={{ color: 'rgb(100, 100, 100)' }}>
            <Typography variant="body2">Politique de confidentialité</Typography>
          </Link>
        </Box>
      </Box>
    </>
  );
};

LandingPageV2.getLayout = (page: ReactElement) => <>{page}</>;

export default withAuth(LandingPageV2, {
  mode: AUTH_MODE.LOGGED_OUT,
});
